// Minutes Panel Component - Phase 3 & 4: Edit Tracking and Edit Restrictions
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, CheckCircle, Loader2, Plus, Save, Send, 
  ChevronRight, ChevronDown, PenLine, Download, Lock, 
  AlertCircle, History, Clock, User, Edit3, Upload, Sparkles
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const MinutesPanel = ({ meetingId, agendaItems, isOpen, onClose, isLive, boardMembers = [] }) => {
  const [minutesData, setMinutesData] = useState(null);
  const [editStatus, setEditStatus] = useState(null);
  const [editHistory, setEditHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');
  const [selectedAgendaItem, setSelectedAgendaItem] = useState(null);
  const [editingNotes, setEditingNotes] = useState({});
  const [openingNotes, setOpeningNotes] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [generatingFollowup, setGeneratingFollowup] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptSourceName, setTranscriptSourceName] = useState('');
  const [generatingTranscriptDraft, setGeneratingTranscriptDraft] = useState(false);
  const [applyingTranscriptDraft, setApplyingTranscriptDraft] = useState(false);
  const [transcriptDraft, setTranscriptDraft] = useState(null);
  const [replaceExistingTranscriptNotes, setReplaceExistingTranscriptNotes] = useState(false);
  const [createTranscriptActionItems, setCreateTranscriptActionItems] = useState(true);
  
  const [showAddAction, setShowAddAction] = useState(false);
  const [newAction, setNewAction] = useState({
    title: '', description: '', assigned_to: '', due_date: '', priority: 'normal'
  });

  // Fetch edit status to determine if editing is allowed
  const fetchEditStatus = useCallback(async () => {
    try {
      const res = await api.get(`/board/meetings/${meetingId}/minutes/edit-status`);
      setEditStatus(res.data);
    } catch (err) {
      console.error('Failed to fetch edit status:', err);
    }
  }, [meetingId]);

  // Fetch edit history
  const fetchEditHistory = useCallback(async () => {
    try {
      const res = await api.get(`/board/meetings/${meetingId}/edit-history?limit=20`);
      setEditHistory(res.data);
    } catch (err) {
      console.error('Failed to fetch edit history:', err);
    }
  }, [meetingId]);

  const fetchMinutes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/board/meetings/${meetingId}/minutes`);
      setMinutesData(res.data);
      setOpeningNotes(res.data.minutes?.opening_notes || '');
      setClosingNotes(res.data.minutes?.closing_notes || '');
      
      const notesMap = {};
      res.data.agenda_notes?.forEach(note => {
        notesMap[note.agenda_item_id] = note.notes || '';
      });
      setEditingNotes(notesMap);
    } catch (err) {
      toast.error('Failed to load minutes');
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    if (isOpen && meetingId) {
      fetchMinutes();
      fetchEditStatus();
    }
  }, [isOpen, meetingId, fetchEditStatus, fetchMinutes]);

  // Fetch history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history' && meetingId && !editHistory) {
      fetchEditHistory();
    }
  }, [activeTab, meetingId, editHistory, fetchEditHistory]);

  // Check if editing is allowed
  const canEdit = editStatus?.can_edit !== false;
  const editLockReason = editStatus?.reason;

  const saveAgendaItemNotes = async (agendaItemId) => {
    if (!canEdit) {
      toast.error(editLockReason || 'Editing is not allowed');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/board/meetings/${meetingId}/agenda/${agendaItemId}/notes`, {
        notes: editingNotes[agendaItemId] || ''
      });
      toast.success('Notes saved');
      fetchMinutes();
      fetchEditHistory(); // Refresh history after edit
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to save notes';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const saveGeneralNotes = async () => {
    if (!canEdit) {
      toast.error(editLockReason || 'Editing is not allowed');
      return;
    }
    
    setSaving(true);
    try {
      await api.put(`/board/meetings/${meetingId}/minutes`, {
        opening_notes: openingNotes,
        closing_notes: closingNotes
      });
      toast.success('Notes saved');
      fetchEditHistory(); // Refresh history after edit
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to save notes';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const createActionItem = async () => {
    if (!canEdit) {
      toast.error(editLockReason || 'Editing is not allowed');
      return;
    }
    
    if (!newAction.title.trim()) {
      toast.error('Action item title is required');
      return;
    }
    
    try {
      await api.post(`/board/meetings/${meetingId}/action-items`, {
        ...newAction,
        agenda_item_id: selectedAgendaItem
      });
      toast.success('Action item created');
      setNewAction({ title: '', description: '', assigned_to: '', due_date: '', priority: 'normal' });
      setShowAddAction(false);
      fetchMinutes();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to create action item';
      toast.error(errorMessage);
    }
  };

  const publishMinutes = async () => {
    if (!window.confirm('Are you sure you want to publish these minutes? This will generate a PDF and save it to the Minutes Archive.')) return;
    
    try {
      await api.post(`/board/meetings/${meetingId}/minutes/publish`);
      toast.success('Minutes published and saved to Minutes Archive');
      fetchMinutes();
      fetchEditStatus();
    } catch (err) {
      toast.error('Failed to publish minutes');
    }
  };

  const generateFollowupTasks = async () => {
    if (generatingFollowup) return;
    setGeneratingFollowup(true);
    try {
      const res = await api.post(`/board/meetings/${meetingId}/generate-followup-tasks`);
      const { created = 0, updated = 0, skipped = 0, orphaned = 0 } = res.data || {};
      toast.success(`Follow-up tasks generated: ${created} created, ${updated} updated`);
      if (skipped || orphaned) {
        toast.message(`Skipped: ${skipped} · Orphaned: ${orphaned}`);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to generate follow-up tasks';
      toast.error(errorMessage);
    } finally {
      setGeneratingFollowup(false);
    }
  };

  const handleTranscriptFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setTranscriptText(text);
      setTranscriptSourceName(file.name);
      toast.success('Transcript loaded');
    } catch (err) {
      toast.error('Failed to read transcript file');
    } finally {
      event.target.value = '';
    }
  };

  const generateTranscriptDraft = async () => {
    if (!canEdit) {
      toast.error(editLockReason || 'Editing is not allowed');
      return;
    }
    if (!transcriptText.trim()) {
      toast.error('Paste or upload a transcript first');
      return;
    }
    setGeneratingTranscriptDraft(true);
    try {
      const res = await api.post(`/board/meetings/${meetingId}/minutes/transcript-draft`, {
        transcript_text: transcriptText,
        source_name: transcriptSourceName || null
      });
      setTranscriptDraft(res.data.draft);
      toast.success('Transcript draft generated');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to generate transcript draft';
      toast.error(errorMessage);
    } finally {
      setGeneratingTranscriptDraft(false);
    }
  };

  const updateTranscriptDraftAgendaNote = (agendaItemId, value) => {
    setTranscriptDraft(prev => ({
      ...prev,
      agenda_notes: (prev?.agenda_notes || []).map(note =>
        note.agenda_item_id === agendaItemId ? { ...note, notes: value } : note
      )
    }));
  };

  const updateTranscriptDraftGeneral = (field, value) => {
    setTranscriptDraft(prev => ({ ...(prev || {}), [field]: value }));
  };

  const updateTranscriptDraftAction = (index, field, value) => {
    setTranscriptDraft(prev => ({
      ...prev,
      action_items: (prev?.action_items || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeTranscriptDraftAction = (index) => {
    setTranscriptDraft(prev => ({
      ...prev,
      action_items: (prev?.action_items || []).filter((_, i) => i !== index)
    }));
  };

  const applyTranscriptDraft = async () => {
    if (!transcriptDraft) return;
    if (!window.confirm('Apply this transcript draft to the meeting minutes?')) return;
    setApplyingTranscriptDraft(true);
    try {
      const res = await api.post(`/board/meetings/${meetingId}/minutes/apply-transcript-draft`, {
        draft: transcriptDraft,
        replace_existing_notes: replaceExistingTranscriptNotes,
        create_action_items: createTranscriptActionItems
      });
      toast.success(`Draft applied: ${res.data.agenda_notes_applied || 0} agenda notes, ${res.data.action_items_created || 0} action items`);
      fetchMinutes();
      fetchEditHistory();
      setActiveTab('notes');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to apply transcript draft';
      toast.error(errorMessage);
    } finally {
      setApplyingTranscriptDraft(false);
    }
  };

  const downloadMinutesPDF = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/board/meetings/${meetingId}/minutes/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'meeting_minutes.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  // Phase 2: Approval Workflow Functions
  const submitForApproval = async () => {
    if (!window.confirm('Submit these minutes for approval? The board chair will need to approve before they become official.')) return;
    
    try {
      await api.post(`/board/meetings/${meetingId}/minutes/submit-for-approval`, {
        notes: 'Submitted for board approval'
      });
      toast.success('Minutes submitted for approval');
      fetchMinutes();
      fetchEditStatus();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to submit for approval';
      toast.error(errorMessage);
    }
  };

  const approveMinutes = async () => {
    if (!window.confirm('Approve these minutes? This will make them official board records and lock them from further editing.')) return;
    
    try {
      await api.post(`/board/meetings/${meetingId}/minutes/approval-action`, {
        action: 'approve',
        comments: 'Approved by board'
      });
      toast.success('Minutes approved and locked');
      fetchMinutes();
      fetchEditStatus();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to approve minutes';
      toast.error(errorMessage);
    }
  };

  const rejectMinutes = async () => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (!reason) {
      toast.error('Rejection reason is required');
      return;
    }
    
    try {
      await api.post(`/board/meetings/${meetingId}/minutes/approval-action`, {
        action: 'reject',
        comments: reason
      });
      toast.success('Minutes rejected and returned to draft');
      fetchMinutes();
      fetchEditStatus();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to reject minutes';
      toast.error(errorMessage);
    }
  };

  const getAgendaItemNotes = (agendaItemId) => {
    return minutesData?.agenda_notes?.find(n => n.agenda_item_id === agendaItemId);
  };

  // Get status badge
  const getStatusBadge = () => {
    const status = minutesData?.minutes?.status;
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700 ml-2"><Lock className="h-3 w-3 mr-1" />Approved & Locked</Badge>;
      case 'pending_approval':
        return <Badge className="bg-blue-100 text-blue-700 ml-2">Pending Approval</Badge>;
      case 'published':
        return <Badge className="bg-purple-100 text-purple-700 ml-2">Published</Badge>;
      case 'draft':
      default:
        return <Badge className="bg-yellow-100 text-yellow-700 ml-2">Draft</Badge>;
    }
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Render edit lock banner
  const renderEditLockBanner = () => {
    if (!editStatus) return null;
    
    const status = editStatus.status;
    
    if (status === 'approved') {
      return (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="edit-lock-banner">
          <Lock className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">Minutes Locked</p>
            <p className="text-sm text-amber-700">
              These minutes have been approved and are locked for compliance. No further edits are allowed.
              {editStatus.approved_by && (
                <span className="block mt-1">Approved by {editStatus.approved_by} on {new Date(editStatus.approved_at).toLocaleDateString()}</span>
              )}
            </p>
          </div>
        </div>
      );
    }
    
    if (status === 'pending_approval' && !editStatus.is_admin) {
      return (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3" data-testid="edit-lock-banner">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-800">Pending Approval</p>
            <p className="text-sm text-blue-700">
              These minutes are pending approval. Only administrators can make edits during the review process.
            </p>
          </div>
        </div>
      );
    }
    
    if (status === 'pending_approval' && editStatus.is_admin) {
      return (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3" data-testid="edit-info-banner">
          <Edit3 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-blue-800">Admin Edit Mode</p>
            <p className="text-sm text-blue-700">
              You can edit these minutes as an administrator while they are pending approval.
            </p>
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-purple-600" />
            Meeting Minutes
            {getStatusBadge()}
          </DialogTitle>
          
          {/* Show rejection reason if minutes were rejected */}
          {minutesData?.minutes?.rejection_reason && minutesData?.minutes?.status === 'draft' && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">
                <strong>Previous submission rejected:</strong> {minutesData.minutes.rejection_reason}
              </p>
            </div>
          )}
          
          {/* Show approval info if approved */}
          {minutesData?.minutes?.status === 'approved' && minutesData?.minutes?.approved_by_name && (
            <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-700">
                <strong>Approved by:</strong> {minutesData.minutes.approved_by_name}
                {minutesData.minutes.approved_at && (
                  <span className="ml-2">on {new Date(minutesData.minutes.approved_at).toLocaleDateString()}</span>
                )}
              </p>
            </div>
          )}
          
          {/* Show pending approval info */}
          {minutesData?.minutes?.status === 'pending_approval' && minutesData?.minutes?.submitted_by_name && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700">
                <strong>Submitted by:</strong> {minutesData.minutes.submitted_by_name}
                {minutesData.minutes.submitted_at && (
                  <span className="ml-2">on {new Date(minutesData.minutes.submitted_at).toLocaleDateString()}</span>
                )}
              </p>
            </div>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Edit Lock Banner */}
            {renderEditLockBanner()}
            
            <div className="flex gap-2 mb-4 border-b">
              <button
                onClick={() => setActiveTab('notes')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'notes' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'
                }`}
                data-testid="tab-notes"
              >
                <FileText className="h-4 w-4 inline mr-2" />Notes
              </button>
              <button
                onClick={() => setActiveTab('actions')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'actions' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'
                }`}
                data-testid="tab-actions"
              >
                <CheckCircle className="h-4 w-4 inline mr-2" />
                Action Items ({minutesData?.action_items?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('transcript')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'transcript' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'
                }`}
                data-testid="tab-transcript"
              >
                <Sparkles className="h-4 w-4 inline mr-2" />
                Transcript Draft
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'history' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'
                }`}
                data-testid="tab-history"
              >
                <History className="h-4 w-4 inline mr-2" />
                Edit History
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {activeTab === 'notes' && (
                <div className="space-y-6">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <Label className="text-purple-900 font-medium">Opening Notes / Call to Order</Label>
                    <Textarea 
                      value={openingNotes} 
                      onChange={(e) => setOpeningNotes(e.target.value)}
                      placeholder="Record opening remarks..." 
                      className="mt-2 min-h-[80px]" 
                      disabled={!canEdit}
                      data-testid="opening-notes-input"
                    />
                    <Button 
                      size="sm" 
                      className="mt-2" 
                      onClick={saveGeneralNotes} 
                      disabled={saving || !canEdit}
                      data-testid="save-opening-notes-btn"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                      {canEdit ? 'Save' : 'Locked'}
                    </Button>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Agenda Item Notes</h4>
                    {agendaItems.map((item) => {
                      const existingNotes = getAgendaItemNotes(item.id);
                      const isSelected = selectedAgendaItem === item.id;
                      return (
                        <div key={item.id} className={`border rounded-lg overflow-hidden ${isSelected ? 'border-purple-300 ring-2 ring-purple-100' : ''}`}>
                          <button onClick={() => setSelectedAgendaItem(isSelected ? null : item.id)}
                            className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm">{item.sort_order}</span>
                              <span className="font-medium">{item.title}</span>
                              {existingNotes?.notes && <Badge variant="outline" className="text-xs">Has notes</Badge>}
                            </div>
                            {isSelected ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                          {isSelected && (
                            <div className="p-4 space-y-3">
                              <Textarea 
                                value={editingNotes[item.id] || ''} 
                                onChange={(e) => setEditingNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                                placeholder="Record discussion points..." 
                                className="min-h-[120px]" 
                                disabled={!canEdit}
                                data-testid={`agenda-notes-input-${item.id}`}
                              />
                              <div className="flex justify-end gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => { setSelectedAgendaItem(item.id); setShowAddAction(true); }}
                                  disabled={!canEdit}
                                >
                                  <Plus className="h-4 w-4 mr-1" />Add Action
                                </Button>
                                <Button 
                                  size="sm" 
                                  onClick={() => saveAgendaItemNotes(item.id)} 
                                  disabled={saving || !canEdit}
                                  data-testid={`save-agenda-notes-btn-${item.id}`}
                                >
                                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                                  {canEdit ? 'Save' : 'Locked'}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <Label>Closing Notes / Adjournment</Label>
                    <Textarea 
                      value={closingNotes} 
                      onChange={(e) => setClosingNotes(e.target.value)}
                      placeholder="Record closing remarks..." 
                      className="mt-2 min-h-[80px]" 
                      disabled={!canEdit}
                      data-testid="closing-notes-input"
                    />
                    <Button 
                      size="sm" 
                      className="mt-2" 
                      onClick={saveGeneralNotes} 
                      disabled={saving || !canEdit}
                      data-testid="save-closing-notes-btn"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                      {canEdit ? 'Save' : 'Locked'}
                    </Button>
                  </div>
                </div>
              )}

              {activeTab === 'actions' && (
                <div className="space-y-4">
                  {showAddAction && canEdit && (
                    <div className="bg-purple-50 rounded-lg p-4 space-y-3">
                      <h4 className="font-medium text-purple-900">New Action Item</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <Label>Title *</Label>
                          <Input value={newAction.title} onChange={(e) => setNewAction(prev => ({ ...prev, title: e.target.value }))} />
                        </div>
                        <div className="col-span-2">
                          <Label>Description</Label>
                          <Textarea value={newAction.description} onChange={(e) => setNewAction(prev => ({ ...prev, description: e.target.value }))} />
                        </div>
                        <div>
                          <Label>Assigned To</Label>
                          <Select value={newAction.assigned_to} onValueChange={(v) => setNewAction(prev => ({ ...prev, assigned_to: v }))}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>
                              {boardMembers.map(m => <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>{m.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Due Date</Label>
                          <Input type="date" value={newAction.due_date} onChange={(e) => setNewAction(prev => ({ ...prev, due_date: e.target.value }))} />
                        </div>
                        <div>
                          <Label>Priority</Label>
                          <Select value={newAction.priority} onValueChange={(v) => setNewAction(prev => ({ ...prev, priority: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={() => setShowAddAction(false)}>Cancel</Button>
                        <Button size="sm" onClick={createActionItem}><Plus className="h-4 w-4 mr-1" />Create</Button>
                      </div>
                    </div>
                  )}
                  {!showAddAction && (
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAddAction(true)}
                      disabled={!canEdit}
                      data-testid="add-action-item-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {canEdit ? 'Add Action Item' : 'Editing Locked'}
                    </Button>
                  )}
                  <div className="space-y-2">
                    {minutesData?.action_items?.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No action items yet</p>
                    ) : minutesData?.action_items?.map((item) => (
                      <div key={item.id} className="border rounded-lg p-3 flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${item.priority === 'urgent' ? 'bg-red-500' : item.priority === 'high' ? 'bg-orange-500' : 'bg-blue-500'}`} />
                        <div className="flex-1">
                          <p className="font-medium">{item.title}</p>
                          {item.description && <p className="text-sm text-gray-600 mt-1">{item.description}</p>}
                          <div className="flex gap-4 mt-2 text-xs text-gray-500">
                            {item.assigned_to_name && <span>Assigned: {item.assigned_to_name}</span>}
                            {item.due_date && <span>Due: {new Date(item.due_date).toLocaleDateString()}</span>}
                          </div>
                        </div>
                        <Badge className={item.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>{item.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'transcript' && (
                <div className="space-y-5" data-testid="transcript-draft-panel">
                  <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-medium text-purple-900 flex items-center gap-2">
                          <Sparkles className="h-4 w-4" />
                          Generate notes from transcript
                        </h4>
                        <p className="text-sm text-purple-700 mt-1">
                          Paste a transcript or upload a text file. Athena will compare it to the agenda and draft reviewable notes/action items.
                        </p>
                      </div>
                      <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-purple-200 bg-white text-sm font-medium text-purple-700 cursor-pointer hover:bg-purple-50">
                        <Upload className="h-4 w-4" />
                        Upload .txt
                        <input type="file" accept=".txt,text/plain,.md,.vtt,.srt" className="hidden" onChange={handleTranscriptFileUpload} />
                      </label>
                    </div>
                    {transcriptSourceName && <p className="text-xs text-purple-700">Loaded: {transcriptSourceName}</p>}
                    <Textarea
                      value={transcriptText}
                      onChange={(e) => setTranscriptText(e.target.value)}
                      placeholder="Paste meeting transcript here..."
                      className="min-h-[180px] bg-white"
                      disabled={!canEdit || generatingTranscriptDraft}
                      data-testid="transcript-input"
                    />
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-gray-500">{transcriptText.trim().length.toLocaleString()} characters</p>
                      <Button onClick={generateTranscriptDraft} disabled={!canEdit || generatingTranscriptDraft || !transcriptText.trim()} data-testid="generate-transcript-draft-btn">
                        {generatingTranscriptDraft ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                        Generate Draft
                      </Button>
                    </div>
                  </div>

                  {transcriptDraft && (
                    <div className="space-y-4">
                      {Array.isArray(transcriptDraft.warnings) && transcriptDraft.warnings.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                          <p className="font-medium mb-1">Review warnings</p>
                          <ul className="list-disc pl-5 space-y-1">
                            {transcriptDraft.warnings.map((warning, index) => <li key={index}>{warning}</li>)}
                          </ul>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border rounded-lg p-3">
                          <Label>Opening notes</Label>
                          <Textarea
                            value={transcriptDraft.opening_notes || ''}
                            onChange={(e) => updateTranscriptDraftGeneral('opening_notes', e.target.value)}
                            className="mt-2 min-h-[90px]"
                          />
                        </div>
                        <div className="border rounded-lg p-3">
                          <Label>Closing notes</Label>
                          <Textarea
                            value={transcriptDraft.closing_notes || ''}
                            onChange={(e) => updateTranscriptDraftGeneral('closing_notes', e.target.value)}
                            className="mt-2 min-h-[90px]"
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-900">Draft agenda notes</h4>
                        {(transcriptDraft.agenda_notes || []).map(note => {
                          const agendaItem = agendaItems.find(item => item.id === note.agenda_item_id);
                          return (
                            <div key={note.agenda_item_id} className="border rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <Label>{agendaItem ? `${agendaItem.sort_order}. ${agendaItem.title}` : 'Agenda item'}</Label>
                                <Badge variant="outline" className="capitalize">{note.confidence || 'medium'} confidence</Badge>
                              </div>
                              <Textarea
                                value={note.notes || ''}
                                onChange={(e) => updateTranscriptDraftAgendaNote(note.agenda_item_id, e.target.value)}
                                className="min-h-[100px]"
                              />
                              {note.decisions?.length > 0 && (
                                <p className="text-xs text-gray-500">Decisions captured: {note.decisions.map(d => d.text || d).join('; ')}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-900">Draft action items</h4>
                        {(transcriptDraft.action_items || []).length === 0 ? (
                          <p className="text-sm text-gray-500 border rounded-lg p-3">No explicit action items found.</p>
                        ) : (
                          (transcriptDraft.action_items || []).map((item, index) => (
                            <div key={index} className="border rounded-lg p-3 space-y-2">
                              <div className="flex gap-2">
                                <Input
                                  value={item.title || ''}
                                  onChange={(e) => updateTranscriptDraftAction(index, 'title', e.target.value)}
                                  placeholder="Action title"
                                  className="font-medium"
                                />
                                <Button variant="ghost" size="sm" onClick={() => removeTranscriptDraftAction(index)}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <Textarea
                                value={item.description || ''}
                                onChange={(e) => updateTranscriptDraftAction(index, 'description', e.target.value)}
                                placeholder="Description"
                                className="min-h-[70px]"
                              />
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <Input
                                  value={item.assigned_to_name || ''}
                                  onChange={(e) => updateTranscriptDraftAction(index, 'assigned_to_name', e.target.value)}
                                  placeholder="Assignee mentioned"
                                />
                                <Input
                                  type="date"
                                  value={item.due_date || ''}
                                  onChange={(e) => updateTranscriptDraftAction(index, 'due_date', e.target.value)}
                                />
                                <Select value={item.priority || 'normal'} onValueChange={(v) => updateTranscriptDraftAction(index, 'priority', v)}>
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="normal">Normal</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                    <SelectItem value="urgent">Urgent</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      <div className="sticky bottom-0 bg-white border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-sm">
                        <div className="space-y-2 text-sm text-gray-700">
                          <label className="flex items-center gap-2">
                            <input type="checkbox" checked={replaceExistingTranscriptNotes} onChange={(e) => setReplaceExistingTranscriptNotes(e.target.checked)} />
                            Replace existing notes instead of filling blanks only
                          </label>
                          <label className="flex items-center gap-2">
                            <input type="checkbox" checked={createTranscriptActionItems} onChange={(e) => setCreateTranscriptActionItems(e.target.checked)} />
                            Create action items from draft
                          </label>
                        </div>
                        <Button onClick={applyTranscriptDraft} disabled={applyingTranscriptDraft || !canEdit} data-testid="apply-transcript-draft-btn">
                          {applyingTranscriptDraft ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                          Apply Draft to Minutes
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Edit History Tab */}
              {activeTab === 'history' && (
                <div className="space-y-4" data-testid="edit-history-panel">
                  {!editHistory ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                    </div>
                  ) : editHistory.total_edits === 0 ? (
                    <div className="text-center py-12">
                      <History className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No edit history yet</p>
                      <p className="text-sm text-gray-400 mt-1">Changes to minutes will appear here</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-gray-900">
                          {editHistory.total_edits} edit{editHistory.total_edits !== 1 ? 's' : ''} recorded
                        </h4>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={fetchEditHistory}
                          className="text-gray-500"
                        >
                          <Loader2 className="h-4 w-4 mr-1" />Refresh
                        </Button>
                      </div>
                      
                      <div className="space-y-3">
                        {editHistory.history.map((edit) => (
                          <div 
                            key={edit.id} 
                            className="border rounded-lg p-3 bg-white hover:bg-gray-50 transition-colors"
                            data-testid={`edit-history-item-${edit.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">
                                  <User className="h-4 w-4" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{edit.edited_by?.name || 'Unknown'}</p>
                                  <p className="text-xs text-gray-500">{formatRelativeTime(edit.edited_at)}</p>
                                </div>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {edit.entity_type === 'minutes' ? 'Minutes' : 
                                 edit.entity_type === 'agenda_notes' ? 'Agenda Notes' : 
                                 edit.entity_type}
                              </Badge>
                            </div>
                            
                            <div className="mt-3 pl-10">
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">{edit.field_name.replace(/_/g, ' ')}</span>
                                {edit.old_value ? ' updated' : ' added'}
                              </p>
                              
                              {edit.old_value && (
                                <div className="mt-2 p-2 bg-red-50 rounded border-l-2 border-red-300">
                                  <p className="text-xs text-gray-500 mb-1">Previous:</p>
                                  <p className="text-sm text-red-700 line-clamp-2">{edit.old_value}</p>
                                </div>
                              )}
                              
                              {edit.new_value && (
                                <div className="mt-2 p-2 bg-green-50 rounded border-l-2 border-green-300">
                                  <p className="text-xs text-gray-500 mb-1">New:</p>
                                  <p className="text-sm text-green-700 line-clamp-2">{edit.new_value}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="border-t pt-4 mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {minutesData?.minutes?.approved_at && (
                  <span className="flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    Locked on {new Date(minutesData.minutes.approved_at).toLocaleString()}
                  </span>
                )}
                {minutesData?.minutes?.published_at && !minutesData?.minutes?.approved_at && (
                  <span>Published on {new Date(minutesData.minutes.published_at).toLocaleString()}</span>
                )}
              </div>
              <div className="flex gap-2">
                {editStatus?.can_generate_followup_tasks && (
                  <Button
                    variant="outline"
                    onClick={generateFollowupTasks}
                    disabled={generatingFollowup}
                    data-testid="generate-followup-btn"
                  >
                    {generatingFollowup ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Generate Follow-up Tasks
                  </Button>
                )}
                <Button variant="outline" onClick={downloadMinutesPDF} disabled={downloading}>
                  {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Download PDF
                </Button>
                
                {/* Draft status - can submit for approval */}
                {minutesData?.minutes?.status === 'draft' && !isLive && (
                  <Button onClick={submitForApproval} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-for-approval-btn">
                    <Send className="h-4 w-4 mr-2" />Submit for Approval
                  </Button>
                )}
                
                {/* Pending approval - can approve or reject */}
                {minutesData?.minutes?.status === 'pending_approval' && (
                  <>
                    <Button onClick={rejectMinutes} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="reject-btn">
                      Reject
                    </Button>
                    <Button onClick={approveMinutes} className="bg-green-600 hover:bg-green-700" data-testid="approve-btn">
                      <CheckCircle className="h-4 w-4 mr-2" />Approve Minutes
                    </Button>
                  </>
                )}
                
                {/* Approved status - can publish to archive */}
                {minutesData?.minutes?.status === 'approved' && !minutesData?.minutes?.published_at && (
                  <Button onClick={publishMinutes} className="bg-purple-600 hover:bg-purple-700" data-testid="publish-btn">
                    <Send className="h-4 w-4 mr-2" />Publish to Archive
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MinutesPanel;
